// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { zodResolver } from '@hookform/resolvers/zod';
import { type SvgIconName, useStepsData } from '@shared/ui-kit';
import { useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import {
  type EmployedProfileFormData,
  EmploymentStatus,
  type ProfessionalProfileFormData,
  professionalProfileSchema,
  type SelfEmployedProfileFormData,
} from '@shared/advisory/account-workflow/types';

export const useProfessionalProfile = () => {
  const { t } = useTranslation();
  const { next } = useStepsData();
  const { getFormData, saveFormData, markStepAsCompleted, isStepCompleted } =
    useAccountWorkflowData();

  const savedData = getFormData('professionalProfile');

  const form = useForm<ProfessionalProfileFormData>({
    resolver: zodResolver(professionalProfileSchema),
    mode: 'onChange',
    defaultValues: savedData,
  });

  const professionalProfile = useWatch({
    control: form.control,
  });

  const employmentStatus = professionalProfile.employmentStatus!;

  const sector = useMemo(() => {
    if ([undefined, EmploymentStatus.NotWorking].includes(employmentStatus)) {
      return undefined;
    }

    return (
      professionalProfile as EmployedProfileFormData | SelfEmployedProfileFormData
    ).sector;
  }, [employmentStatus, professionalProfile]);

  const employmentStatusOptions = useMemo(() => {
    const items = t('activity-types', {
      ns: 'inversis-activity-types',
      returnObjects: true,
    }) as Record<string, string>;

    const statusConfig: Record<
      EmploymentStatus,
      { icon: SvgIconName; subtitleKey: string }
    > = {
      [EmploymentStatus.Employed]: {
        icon: 'User',
        subtitleKey:
          'account-opening.professional-profile.employment-status.options.employed.subtitle',
      },
      [EmploymentStatus.SelfEmployed]: {
        icon: 'Briefcase',
        subtitleKey:
          'account-opening.professional-profile.employment-status.options.self-employed.subtitle',
      },
      [EmploymentStatus.NotWorking]: {
        icon: 'UserX',
        subtitleKey:
          'account-opening.professional-profile.employment-status.options.not-working.subtitle',
      },
    };

    return [
      EmploymentStatus.Employed,
      EmploymentStatus.SelfEmployed,
      EmploymentStatus.NotWorking,
    ].map(status => ({
      value: status,
      label: items[status] ?? status,
      icon: statusConfig[status].icon,
      subtitleKey: statusConfig[status].subtitleKey,
    }));
  }, [t]);

  const sectorOptions = useMemo(() => {
    if ([undefined, EmploymentStatus.NotWorking].includes(employmentStatus)) {
      return [];
    }

    const items = t('sectors-by-activity-type', {
      ns: 'inversis-sectors',
      returnObjects: true,
    }) as Record<string, Record<string, string>>;

    return Object.entries(items[employmentStatus]).map(([value, label]) => ({
      value,
      label,
    }));
  }, [employmentStatus, t]);

  const activityOptions = useMemo(() => {
    if (
      [undefined, EmploymentStatus.NotWorking].includes(employmentStatus) ||
      [undefined, ''].includes(sector)
    )
      return [];

    const items = t('activities-by-activity-type-and-sector', {
      ns: 'inversis-activities',
      returnObjects: true,
    }) as Record<string, Record<string, Record<string, string>>>;

    return Object.entries(items[employmentStatus]?.[sector!] ?? {}).map(
      ([value, label]) => ({
        value,
        label,
      }),
    );
  }, [employmentStatus, sector, t]);

  const isFormValid = useCallback(() => {
    return form.formState.isValid;
  }, [form.formState.isValid]);

  const onEmploymentStatusChange = useCallback(
    (newStatus: EmploymentStatus) => {
      form.setValue('employmentStatus', newStatus);

      switch (newStatus) {
        case EmploymentStatus.Employed:
          form.setValue('companyActivity', '');
          form.setValue('companyName', '');
          form.setValue('sector', '');
          break;

        case EmploymentStatus.SelfEmployed:
          form.setValue('businessName', '');
          form.setValue('sector', '');
          form.setValue('mainActivity', '');
          break;

        case EmploymentStatus.NotWorking:
          break;
      }
    },
    [form],
  );

  const onNext = useCallback(
    async (data: ProfessionalProfileFormData) => {
      if (isStepCompleted('professional-profile', form.formState.isDirty)) {
        next();
        return;
      }

      saveFormData('professionalProfile', data);

      markStepAsCompleted('professional-profile');
      next();
    },
    [
      form.formState.isDirty,
      markStepAsCompleted,
      next,
      saveFormData,
      isStepCompleted,
    ],
  );

  return {
    onNext,
    onEmploymentStatusChange,
    isFormValid,
    form,
    professionalProfile,
    employmentStatusOptions,
    employmentStatus,
    sectorOptions,
    activityOptions,
    t,
    sector,
  };
};
